/* Product-page policy presentation only. Native STOQ/cart/order data stays intact. */
(() => {
  'use strict';
  if (window.PMVPreorderPolicy || !document.body.classList.contains('template-product')) return;
  let guard, copy;
  try {
    guard = JSON.parse(document.getElementById('pmv-preorder-guard-bootstrap').textContent);
    copy = JSON.parse(document.getElementById('pmv-preorder-policy-copy').textContent);
  } catch (_) { return; }
  const key = value => String(value || '').split('/').pop();
  const scope = new Set((guard.scope || []).map(row => key(row.variantId)));
  if (guard.initial?.schema !== 1 || !guard.initial.productVariants?.some(row => row.enabled === true && scope.has(key(row.variantId)))) return;
  if (![copy.payment, copy.cancellation, copy.summary, copy.paymentLabel, copy.cancellationLabel].every(value => typeof value === 'string' && value.trim()) || !/^[0-9a-f]{8}$/.test(copy.policyFingerprint || '')) return;
  const root = document.getElementById('MainContent');
  if (!root) return;
  const selector = '.product__info-container .restock-rocket-preorder-description';
  const marker = 'data-pmv-policy-ready';
  const owned = '[data-pmv-preorder-policy]';
  const records = new Map();
  let frame = 0, stopped = false;

  function visible(element) {
    if (!element || !element.getClientRects().length) return false;
    for (let node = element; node && node.nodeType === 1; node = node.parentElement) {
      const style = getComputedStyle(node);
      if (node.hidden || node.getAttribute('aria-hidden') === 'true' || style.display === 'none' ||
          style.visibility === 'hidden' || style.visibility === 'collapse' || Number(style.opacity) === 0) return false;
    }
    return true;
  }
  function originalVisible(card, source) {
    // Measure the app's own visibility without our optional presentation rule.
    // This private marker is deliberately outside the observer's attribute list.
    const marked = card.hasAttribute(marker);
    if (marked) card.removeAttribute(marker);
    try { return !!source.textContent.trim() && visible(card) && visible(source); }
    finally { if (marked) card.setAttribute(marker, ''); }
  }
  function policyFingerprint(source) {
    const clone = source.cloneNode(true);
    clone.querySelectorAll('br').forEach(node => node.replaceWith(document.createTextNode(' ')));
    const text = clone.textContent.replace(/\s+/g, ' ').trim();
    let value = 2166136261;
    for (const character of text) value = Math.imul(value ^ character.charCodeAt(0), 16777619) >>> 0;
    return value.toString(16).padStart(8, '0');
  }
  function restore(card) {
    card.removeAttribute(marker);
    const record = records.get(card);
    if (record) record.component.remove();
    // A cloned app card can inherit our prior presentation nodes.
    for (const child of [...card.children]) if (child.matches(owned)) child.remove();
    records.delete(card);
  }
  function build(source, open) {
    const component = document.createElement('div');
    component.className = 'pmv-preorder-policy';
    component.setAttribute('data-pmv-preorder-policy', '');
    component.style.color = getComputedStyle(source).color;
    for (const [label, value] of [[copy.paymentLabel, copy.payment], [copy.cancellationLabel, copy.cancellation]]) {
      const line = document.createElement('p');
      line.className = 'pmv-preorder-policy__essential';
      const title = document.createElement('strong');
      title.className = 'pmv-preorder-policy__label';
      title.textContent = label + ': ';
      line.append(title, document.createTextNode(value));
      component.append(line);
    }
    const details = document.createElement('details');
    details.className = 'pmv-preorder-policy__details';
    details.open = !!open;
    const summary = document.createElement('summary');
    summary.className = 'pmv-preorder-policy__summary';
    const label = document.createElement('span');
    label.textContent = copy.summary;
    const chevron = document.createElement('span');
    chevron.className = 'pmv-preorder-policy__chevron';
    chevron.setAttribute('aria-hidden', 'true');
    summary.append(label, chevron);
    const full = document.createElement('div');
    full.className = 'pmv-preorder-policy__full';
    // Clone actual policy children verbatim; never move or rewrite app children.
    for (const child of source.childNodes) full.append(child.cloneNode(true));
    if (full.innerHTML !== source.innerHTML || !full.textContent.trim()) throw new Error('Policy clone incomplete');
    details.append(summary, full);
    component.append(details);
    return { component, details, full };
  }
  function update(card) {
    let prior = records.get(card);
    const source = card.querySelector('.preorder-description-text');
    if (!source || !originalVisible(card, source)) { restore(card); return; }
    if (policyFingerprint(source) !== copy.policyFingerprint) { restore(card); return; }
    const signature = source.innerHTML;
    if (prior && prior.source === source && prior.signature === signature && prior.component.parentElement === card) {
      prior.component.style.color = getComputedStyle(source).color;
      return;
    }
    const open = prior?.details.open;
    restore(card);
    let next;
    try {
      next = build(source, open);
      card.append(next.component);
      if (next.component.parentElement !== card || next.full.innerHTML !== source.innerHTML) throw new Error('Policy insertion incomplete');
      records.set(card, { ...next, source, signature });
      // Hide only after a complete, connected replacement exists.
      card.setAttribute(marker, '');
    } catch (_) {
      next?.component.remove();
      restore(card);
    }
  }
  function reconcile() {
    frame = 0;
    if (stopped) return;
    for (const card of records.keys()) if (!card.isConnected || !root.contains(card)) restore(card);
    for (const card of root.querySelectorAll(selector)) {
      try { update(card); }
      catch (_) { restore(card); }
    }
  }
  function schedule() {
    if (!stopped && !frame) frame = requestAnimationFrame(reconcile);
  }
  function insideOwn(node) {
    const element = node?.nodeType === 1 ? node : node?.parentElement;
    return !!element?.closest(owned);
  }
  const observer = new MutationObserver(mutations => {
    if (mutations.some(mutation => {
      if (insideOwn(mutation.target)) return false;
      if (mutation.type !== 'childList') return true;
      const changed = [...mutation.addedNodes, ...mutation.removedNodes];
      return mutation.removedNodes.length > 0 || !changed.length || !changed.every(node => node.nodeType === 1 && node.matches(owned));
    })) schedule();
  });
  observer.observe(root, { childList: true, subtree: true, characterData: true,
    attributes: true, attributeFilter: ['hidden', 'style', 'class', 'aria-hidden'] });
  window.PMVPreorderPolicy = {
    refresh: schedule,
    destroy() {
      stopped = true;
      cancelAnimationFrame(frame);
      observer.disconnect();
      for (const card of [...records.keys()]) restore(card);
      delete window.PMVPreorderPolicy;
    }
  };
  schedule();
})();
